import { supabase } from '../../supabase';
import { pkTodayKey } from '../../utils/pkTime';
import { cachedQuery } from '../cache/queryCache';
import { saleRowLeft } from '../../pages/receptionist/sales/salePaymentUtils';
import { rowToMember, rowToPayment } from '../receptionist/mappers';

function isRpcMissing(error) {
  const msg = String(error?.message || error || '').toLowerCase();
  return (
    msg.includes('could not find the function')
    || msg.includes('function public.get_customer_ledger_page')
    || msg.includes('schema cache')
    || (msg.includes('get_customer_ledger_page') && msg.includes('does not exist'))
  );
}

function isoDate(value) {
  const d = String(value || '').slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(d) ? d : null;
}

export function mapCustomerLedgerPaymentRow(row) {
  const members = row.members || {};
  const payment = rowToPayment(row, {
    ...members,
    gym_member_id: members.gym_member_id,
    full_name: members.full_name,
    baseGymId: members.gym_member_id,
    trainer: members.trainer,
  });
  const left = Number(row.pending_amount);
  return {
    ...payment,
    date: row.sale_day || payment.date || row.created_at,
    trainer: members.trainer || row.trainer || payment.trainer || '',
    branch: members.branch || row.branch_name || row.branch || '',
    duration: row.duration || payment.duration,
    startDate: row.start_date || payment.startDate,
    endDate: row.end_date || payment.endDate,
    pendingAmount: Number.isFinite(left) ? left : saleRowLeft(payment),
  };
}

export function mapCustomerLedgerMember(row) {
  if (!row) return null;
  return rowToMember({
    id: row.id,
    gym_id: row.gym_id,
    full_name: row.full_name,
    gym_member_id: row.gym_member_id,
    phone: row.phone,
    email: row.email,
    branch: row.branch,
    trainer: row.trainer,
    status: row.status,
    package: row.package,
    profile: {
      package: row.package,
      trainer: row.trainer,
      branch: row.branch,
    },
  });
}

export async function fetchCustomerLedgerPageRpc({
  gymId,
  memberId,
  dateFrom = '',
  dateTo = '',
  branch = '',
  receptionistScope = false,
} = {}) {
  if (!gymId || !memberId) return null;

  const today = pkTodayKey();
  const from = receptionistScope ? today : isoDate(dateFrom);
  const to = receptionistScope ? today : isoDate(dateTo);
  const branchValue = String(branch || '').trim();
  const pBranch = receptionistScope
    ? ((!branchValue || branchValue === 'all') ? null : branchValue)
    : ((!branchValue || branchValue === 'all') ? null : branchValue);
  const cacheKey = `gym:${gymId}:customer-ledger:${memberId}:${from || 'all'}:${to || 'all'}:${pBranch || 'all'}`;

  return cachedQuery(cacheKey, async () => {
    const { data, error } = await supabase.rpc('get_customer_ledger_page', {
      p_gym_id: gymId,
      p_member_id: memberId,
      p_date_from: from,
      p_date_to: to,
      p_branch: pBranch,
    });

    if (error) {
      if (isRpcMissing(error)) return null;
      throw error;
    }
    const payload = typeof data === 'string'
      ? (() => { try { return JSON.parse(data); } catch { return null; } })()
      : data;
    if (!payload) return null;
    if (payload.error === 'invalid_params') return null;
    if (payload.error === 'not_found') {
      return { member: null, payments: [], totalPayments: 0, collected: 0, fromRpc: true, notFound: true };
    }
    if (payload.error) return null;

    return {
      member: mapCustomerLedgerMember(payload.member),
      payments: (payload.payments || []).map(mapCustomerLedgerPaymentRow),
      totalPayments: Number(payload.total_payments) || 0,
      collected: Number(payload.collected) || 0,
      fromRpc: true,
    };
  }, 45_000);
}
